/**
 * Friendly error messages
 *
 * Convex (and other backends) surface technical errors that mean nothing to a
 * shop owner — e.g.:
 *
 *   [CONVEX M(orders/swaps:swapOrderItems)] [Request ID: 91aef5d2…] Server Error
 *   Uncaught Error: Insufficient stock at handler (../convex/orders/swaps.ts:90)
 *
 * These helpers turn any caught error into a clean, human message:
 *   - If the backend threw a `ConvexError("nice message")`, that message is
 *     delivered verbatim (developers control it, so it's already friendly).
 *   - Otherwise we strip the Convex wrapper, request IDs, "Server Error",
 *     and stack frames. If nothing meaningful remains, we return a generic
 *     fallback instead of leaking internals.
 *
 * Prefer `getFriendlyErrorMessage(error)` when you have the raw error object.
 * `sanitizeErrorMessage(string)` is used by the toast wrapper to clean message
 * strings that have already been stringified at the call site.
 */

const DEFAULT_MESSAGE = "Something went wrong. Please try again.";

// Phrases that, on their own, carry no useful meaning for the user.
const NOISE_ONLY = /^(server error|internal server error|error|failed|unknown error|\.?)$/i;

/**
 * Clean a message STRING (already extracted from an error). Strips Convex
 * technical noise and returns a friendly message, or the generic fallback when
 * nothing meaningful is left.
 */
export function sanitizeErrorMessage(input: string | null | undefined): string {
  if (!input) return DEFAULT_MESSAGE;
  let msg = String(input).trim();

  // If the message embeds a thrown error after "Uncaught …Error:", keep only
  // the part the developer actually wrote.
  const uncaught = msg.match(/Uncaught\s+(?:Convex)?Error:\s*([\s\S]*)/i);
  if (uncaught && uncaught[1]) msg = uncaught[1].trim();

  // Drop Convex function/request tags: "[CONVEX M(path)]", "[Request ID: …]".
  msg = msg.replace(/\[CONVEX[^\]]*\]/gi, " ");
  msg = msg.replace(/\[Request ID:[^\]]*\]/gi, " ");

  // Drop trailing stack frames: " at handler (../convex/…:90:9)".
  msg = msg.replace(/\s+at\s+[^\n]*$/i, " ");

  // Remove standalone technical phrases.
  msg = msg.replace(/\bCalled by client\b/gi, " ");
  msg = msg.replace(/\bServer Error\b/gi, " ");
  msg = msg.replace(/\bUncaught (?:Convex)?Error:?/gi, " ");

  // Collapse whitespace + trim leftover punctuation.
  msg = msg.replace(/\s+/g, " ").trim();
  msg = msg.replace(/^[-–—:;,.\s]+/, "").replace(/[-–—:;,\s]+$/, "").trim();

  if (!msg || NOISE_ONLY.test(msg)) return DEFAULT_MESSAGE;
  return msg;
}

/**
 * What the user reads when the server hit something unexpected (a schema
 * mismatch, a missing document…). The real text travels alongside for the
 * error log — see `describeErrorForReport` — but is never shown here.
 */
export const INTERNAL_ERROR_MESSAGE =
  "Something on our side stopped this from saving. It has been reported to Kuttos automatically — please try again in a moment.";

/** Pull a friendly string out of a ConvexError's `data` payload. */
function extractFromData(data: unknown): string {
  if (typeof data === "string") return sanitizeErrorMessage(data);
  if (data && typeof data === "object") {
    const d = data as Record<string, unknown>;
    // Surfaced internal failure (convex/helpers/surfaceErrors.ts): the
    // message is a developer dump, so the user gets the friendly line.
    if (d.kind === "internal") return INTERNAL_ERROR_MESSAGE;
    if (typeof d.message === "string") return sanitizeErrorMessage(d.message);
    if (typeof d.error === "string") return sanitizeErrorMessage(d.error);
  }
  return DEFAULT_MESSAGE;
}

export type ErrorReport = {
  /** The real error text, as detailed as the client can see it. */
  message: string;
  /** Convex request id when present — finds the server-side log line. */
  requestId?: string;
  /** Function or step the server named, for surfaced internal errors. */
  where?: string;
  /** True when the server marked this as an unexpected internal failure. */
  internal: boolean;
};

/**
 * Everything worth writing to the platform error log about a caught error —
 * the opposite of `getFriendlyErrorMessage`: as much detail as possible.
 * A surfaced internal error carries the server's real message in its data;
 * anything else contributes whatever text it has.
 */
export function describeErrorForReport(error: unknown): ErrorReport {
  const anyErr = error as any;
  const outerMessage: string = typeof anyErr?.message === "string" ? anyErr.message : String(error ?? "");
  const requestId = outerMessage.match(/Request ID:\s*([A-Za-z0-9_-]+)/i)?.[1];
  const data = anyErr?.data;
  if (data && typeof data === "object") {
    const d = data as Record<string, unknown>;
    if (d.kind === "internal" && typeof d.message === "string") {
      return { message: d.message, requestId, where: typeof d.where === "string" ? d.where : undefined, internal: true };
    }
    if (typeof d.message === "string") return { message: d.message, requestId, internal: false };
    return { message: JSON.stringify(d), requestId, internal: false };
  }
  if (typeof data === "string") return { message: data, requestId, internal: false };
  return { message: outerMessage || "Unknown error", requestId, internal: false };
}

/**
 * Turn ANY caught value into a friendly, user-safe message.
 * Use this in catch blocks: `toast.error(getFriendlyErrorMessage(err))`.
 */
export function getFriendlyErrorMessage(error: unknown): string {
  if (error == null) return DEFAULT_MESSAGE;

  // Plain string thrown.
  if (typeof error === "string") return sanitizeErrorMessage(error);

  const anyErr = error as any;

  // ConvexError (or anything that walks like one): developer-supplied `data`
  // is delivered to the client untouched, so prefer it.
  if (
    anyErr?.name === "ConvexError" ||
    (anyErr?.data !== undefined && anyErr?.data !== null && typeof anyErr?.message === "string" && /ConvexError/i.test(anyErr.message))
  ) {
    return extractFromData(anyErr.data);
  }

  // Regular Error / object with a message.
  if (typeof anyErr?.message === "string") return sanitizeErrorMessage(anyErr.message);

  return DEFAULT_MESSAGE;
}

export const DEFAULT_ERROR_MESSAGE = DEFAULT_MESSAGE;
